'use strict';

const d3 = require('d3');
const _ = require('underscore');
const Backbone = require('backbone');
const VectorUtil = require('common/vector-util');
const Util = require('common/util');
const TransientButtonView = require('common/ui/view/svg/transient-button');
const TransientSelectorView = require('common/ui/view/svg/transient-selector');
const Diagrams = require('system/domain/value/diagrams');
const ElementAction = require('system/domain/action/element');
const ElementRepository = require('system/domain/repository/element');
const NodeTypeMenu = require('system/ui/view/menu/node-type');
const LinkTypeMenu = require('system/ui/view/menu/link-type');
const NodeEditMenu = require('system/ui/view/menu/node-edit');

module.exports = Backbone.Model.extend({
    initialize: function(params) {
        _.bindAll(this,
            'removeMenu',
            'addNode',
            'changeNodeType',
            'renderAddNodeMenu',
            'renderChangeNodeTypeMenu',
            'addLink',
            'changeLinkType',
            'renderChangeLinkTypeMenu'
        );
        this._diagram = params.diagram;
        this._canvas = params.canvas;
        this._context = params.context;
        this._channel = params.channel;
        this._user = params.user;
        this._menu = null;
        this._selector = null;
    },

    removeMenu: function() {
        if(this._menu) {
            this._menu.remove();
        }
        if(this._selector) {
            this._selector.remove();
        }
    },

    // Node 関連のメニュー
    addNode: function(pos, scale) {
        const createNode = type => {
            const id = Util.generateId();
            ElementAction.createNode({
                context: this._context,
                nodeMeta: {
                    id: id,
                    x: pos.x,
                    y: pos.y
                },
                node: {
                    id: id,
                    type: type,
                    name: '',
                    owners: [this._user.id.toString()]
                }
            });
            ElementAction.startEditNode({
                context: this._context,
                node: {id: id}
            });
            const node = ElementRepository.getNodes({
                context: this._context
            }).get(id);
            setTimeout(() => {
                node.select();
            });
        };

        const type = Diagrams.getNodes(this._diagram.get('type'))[0];
        _.bind(createNode, this)(type);
    },

    changeNodeType: function(node) {
        this.removeMenu();
        const menu = new NodeTypeMenu({
            diagram: this._diagram,
            model: node,
            r: 60
        });
        this._menu = menu;
        menu.listenTo(node, 'deselect', menu.remove);

        const idParams = this._diagram.idParams();
        menu.on('select:type', (type, menu) => {
            menu.remove();
            const fromType = node.get('type');
            ElementAction.updateNode({
                context: this._context,
                node: {
                    id: node.id,
                    type: type
                }
            });
        });

        const meta = this._diagram.getNodeMeta(node.id);
        const pos = {
            x: meta.x,
            y: meta.y
        };

        menu.render(
            d3.select('g.menus'),
            pos,
            this._canvas.scale(),
            {
                startAngle: 90,
                circle: true
            }
        );
        this._canvas.on('zoom', (pos, scale) => {
            if(menu) {
                menu.unzoom(scale);
            }
        });
    },

    renderAddNodeMenu: function(pos, scale) {
        this.removeMenu();
        const menu = new TransientButtonView({
            ring: true,
            pos: pos,
            scale: scale,
            iconClass: 'fa fa-plus'
        });
        this._menu = menu;
        d3.select('g.menus').append(menu.render().el);

        const selector = new TransientSelectorView({
            pos: pos,
            scale: scale
        });
        d3.select('g.menus').append(selector.render().el);
        selector.on('click', () => {
            menu.remove();
            selector.remove();
            this.renderRectSelector(pos, scale);
        });
        this._selector = selector;

        menu.on('click', () => {
            this.addNode(pos, scale);
        });

        this._canvas.on('zoom',(pos, scale) => {
            if(menu) {
                menu.unzoom(scale);
            }
        });
    },

    renderChangeNodeTypeMenu: function(pos, scale, node) {
        this.removeMenu();
        const menu = new TransientButtonView({
            pos: pos,
            scale: scale,
            iconClass: 'fa fa-refresh'
        });
        this._menu = menu;
        d3.select('g.menus').append(menu.render().el);

        menu.on('click', () => {
            this.changeNodeType(node)
        });

        this._canvas.on('zoom', (pos, scale) => {
            if(menu) {
                menu.unzoom(scale);
            }
        });
    },

    renderNodeEditMenu: function(node, scale) {
        this.removeMenu();
        const meta = this._diagram.getNodeMeta(node.id);
        const menu = new NodeEditMenu({
            diagram: this._diagram,
            model: node,
            r: 60,
            isZone: meta.get('is_zone')
        });

        this._menu = menu;
        menu.listenTo(node, 'deselect', menu.remove);

        const self = this;
        menu.on('type', menu => {
            menu.remove();
            self.changeNodeType(node);
        });
        menu.on('zone', menu => {
            menu.remove();
            let shape = meta.get('shape');
            if(!shape) {
                shape = {
                    kind: 'rect',
                    attr: {
                        width: 1280,
                        height: 1280
                    }
                };
            }
            ElementAction.updateNode({
                context: this._context,
                nodeMeta: {
                    id: meta.id,
                    is_zone: true,
                    shape: shape
                }
            });
        });
        menu.on('node', menu => {
            menu.remove();
            ElementAction.updateNode({
                context: this._context,
                nodeMeta: {
                    id: meta.id,
                    is_zone: false
                }
            });
        });

        menu.on('comment', menu => {
            menu.remove();
            this._channel.trigger('append:comment:record', node);
        });
        menu.on('cancel', menu => {
            menu.remove();
            ElementAction.cancelEditNode({
                context: this._context,
                node: {id: node.id}
            });
        });
        menu.on('moveto', menu => {
            menu.remove();
            this._channel.request('canvas:zoomTo', node);
        });

        const pos = {
            x: meta.x,
            y: meta.y
        };

        menu.render(
            d3.select('g.menus'),
            pos,
            this._canvas.scale(),
            {
                startAngle: 90,
                circle: true
            }
        );
        this._canvas.on('zoom', (pos, scale) => {
            if(menu) {
                menu.unzoom(scale);
            }
        });
    },

    renderNodeEditMenuEntry: function(node, scale) {
        this.removeMenu();
        const meta = this._diagram.getNodeMeta(node.id);
        const pos = {
            x: meta.x,
            y: meta.y
        };
        const menu = new TransientButtonView({
            pos: pos,
            scale: scale,
            iconClass: 'fa fa-ellipsis-h'
        });
        this._menu = menu;
        d3.select('g.menus').append(menu.render().el);

        menu.on('click', () => {
            this.renderNodeEditMenu(node)
        });

        this._canvas.on('zoom', (pos, scale) => {
            if(menu) {
                menu.unzoom(scale);
            }
        });
    },

    // Link 関連のメニュー
    addLink: function(source, target, pos) {
        const createLink = type => {
            const id = Util.generateId();
            ElementAction.createLink({
                context: this._context,
                linkMeta: {
                    id: id
                },
                link: {
                    id: id,
                    type: type,
                    source: source.id,
                    target: target.id
                }
            });
            ElementAction.startEditLink({
                context: this._context,
                link: {id: id}
            });
            const link = ElementRepository.getLinks({
                context: this._context
            }).get(id);
            const scale = this._canvas.scale();
            setTimeout(() => {
                this.renderChangeLinkTypeMenu(
                    pos, scale, link);
            });
        };

        const type = Diagrams.getLinks(
            source.get('type'), target.get('type'))[0];
        _.bind(createLink, this)(type);
    },

    changeLinkType: function(pos, scale, link) {
        this.removeMenu();
        const menu = new LinkTypeMenu({
            model: link,
            r: 60,
            source: link.get('source'),
            target: link.get('target')
        });
        this._menu = menu;
        menu.listenTo(link, 'deselect', menu.remove);

        if(!pos) {
            const source = this._diagram.getNodeMeta(link.source.id);
            const target = this._diagram.getNodeMeta(link.target.id);
            pos = VectorUtil.interpolate(source, target);
        }
        pos.scale = null;

        const idParams = this._diagram.idParams();
        menu.on('select:type', (type, menu) => {
            menu.remove();
            const fromType = link.get('type');
            ElementAction.updateLink({
                context: this._context,
                link: {
                    id: link.id,
                    type: type
                }
            });
        });

        menu.render(
            d3.select('g.menus'),
            pos,
            this._canvas.scale(),
            {
                startAngle: 90,
                circle: true
            }
        );
        this._canvas.on('zoom', (pos, scale) => {
            if(menu) {
                menu.unzoom(scale);
            }
        });
    },

    renderChangeLinkTypeMenu: function(pos, scale, link) {
        this.removeMenu();
        const menu = new TransientButtonView({
            pos: pos.toObject(),
            scale: scale,
            iconClass: 'fa fa-refresh'
        });
        this._menu = menu;
        d3.select('g.menus').append(menu.render().el);

        menu.on('click', () => {
            this.changeLinkType(pos.toObject(), scale, link);
        });

        this._canvas.on('zoom', (pos, scale) => {
            if(menu) {
                menu.unzoom(scale);
            }
        });
    }
});



// WEBPACK FOOTER //
// ./system/ui/view/menu/canvas.js